import useGetCustomerAddresses from '@/api/queries/customers/useGetCustomerAddresses';
import { Address as _Address, UpdateAddressContactInput } from '@/gql/graphql';
import AddEditAddressContactsControl from '@/ui/components/addressControls/AddEditAddressContactsControl';
import ManuallyAddEditAddressControl from '@/ui/components/addressControls/ManualAddEditAddressControl';
import {
  Button,
  Combobox,
  Conditional,
  Modal,
  ModalProps,
  OptionGroup,
} from '@payaca/ambrosia';

import { useDebounce } from '@payaca/hooks';
import UntitledIcon from '@payaca/untitled-icons';
import { FC, useCallback, useMemo, useState } from 'react';

type Address = Pick<
  _Address,
  | 'id'
  | 'line1'
  | 'line2'
  | 'city'
  | 'administrativeArea'
  | 'country'
  | 'postalCode'
  | 'fullLocalAddress'
  | 'coordinatesSource'
  | 'contacts'
>;

export interface IProps extends Omit<ModalProps, 'onBack' | 'title'> {
  title?: string;
  selectedAddress?: Address;
  customerId: string;
  onAddressChange?: (value: string) => void;
  onChange?: (
    address: Omit<Address, 'contacts'> & {
      contacts: UpdateAddressContactInput[];
    }
  ) => void;
  onAddNewAddress?: () => void;
  disableAddressChange?: boolean;
  onDone?: () => void;
  isSiteAddress?: boolean;
}

const PAGE_SIZE = 25;
const ADD_DIFFERENT_ADDRESS = 'add-different-address';

const SelectEditAddressAndAddressContactsModal: FC<IProps> = (props) => {
  const {
    title = 'Edit address',
    selectedAddress,
    customerId,
    onAddressChange,
    onChange,
    onAddNewAddress,
    disableAddressChange = false,
    onDone,
    isSiteAddress = false,
    ...rest
  } = props;

  const [isManualEdit, setIsManualEdit] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  useDebounce(
    () => {
      setDebouncedSearchQuery(searchQuery);
    },
    300,
    [searchQuery]
  );

  const {
    addresses: paginatedAddresses,
    isLoading: isLoadingAddresses,
    isFetching: isFetchingAddresses,
  } = useGetCustomerAddresses(
    customerId,
    { searchTerm: debouncedSearchQuery || undefined },
    { limit: PAGE_SIZE, offset: 0 }
  );

  const addresses = useMemo(() => {
    if (!paginatedAddresses) return [];
    return paginatedAddresses.items.map((address) => ({
      label: address.fullLocalAddress || '',
      value: address.id,
    }));
  }, [paginatedAddresses]);

  // Show loading indicator when fetching new results (but not during initial load)
  const isSearching = isFetchingAddresses && !isLoadingAddresses;

  const [comboBoxOptions, comboBoxGroups] = useMemo(() => {
    const _comboBoxOptions = addresses.map((address) => ({
      groupId: 'customerAddresses',
      ...address,
    }));
    let _comboBoxGroups: OptionGroup[] = [];

    if (onAddNewAddress) {
      _comboBoxOptions.push({
        groupId: 'add-customer-address',
        label: 'Add a new customer address',
        value: ADD_DIFFERENT_ADDRESS,
      });

      _comboBoxGroups = [
        { id: 'customerAddresses', label: 'Customer addresses' },
        { id: 'add-customer-address', label: 'Other' },
      ];
    }

    return [_comboBoxOptions, _comboBoxGroups];
  }, [addresses, onAddNewAddress]);

  const handleUpdateContacts = useCallback(
    (updatedContacts: UpdateAddressContactInput[]) => {
      if (selectedAddress) {
        onChange?.({
          ...selectedAddress,
          contacts: updatedContacts,
        });
      }
    },
    [selectedAddress, onChange]
  );

  return (
    <Modal
      title={title}
      onBack={isManualEdit ? () => setIsManualEdit(false) : undefined}
      {...rest}
    >
      <Conditional
        condition={!isManualEdit}
        fallback={
          <ManuallyAddEditAddressControl
            isInModal
            isSiteAddress={isSiteAddress}
            address={selectedAddress}
            onSave={(_addressWithNotContacts) => {
              if (selectedAddress) {
                onChange?.({
                  ...selectedAddress,
                  ..._addressWithNotContacts,
                });

                setIsManualEdit(false);
              }
            }}
            onClose={() => {
              setIsManualEdit(false);
            }}
          />
        }
      >
        <Modal.Body>
          <div className="flex flex-col gap-4">
            <div className="relative">
              <Combobox<string, false>
                value={selectedAddress?.id}
                optionGroups={comboBoxGroups}
                disabled={disableAddressChange || isLoadingAddresses}
                placeholder="Type to search"
                inputPlaceholder="Type to search"
                options={comboBoxOptions}
                customButtonContent={selectedAddress?.fullLocalAddress}
                onChange={(value) => {
                  // Clearing the search input is not an address selection.
                  if (!value) return;
                  if (value === 'add-different-address') {
                    onAddNewAddress?.();
                    return;
                  }

                  onAddressChange?.(value);
                }}
                query={searchQuery}
                setQuery={setSearchQuery}
              />
              {isSearching && (
                <div className="absolute right-10 top-1/2 -translate-y-1/2">
                  <UntitledIcon
                    name="loading-02.3"
                    className="h-4 w-4 animate-spin text-gray-400"
                  />
                </div>
              )}
            </div>

            <Conditional condition={!selectedAddress && !!onAddNewAddress}>
              <div>
                <Button
                  variant="link"
                  onClick={() => {
                    onAddNewAddress?.();
                  }}
                >
                  Add a different address
                </Button>
              </div>
            </Conditional>

            <Conditional condition={!!selectedAddress}>
              <div>
                <Button
                  variant="link"
                  onClick={() => {
                    setIsManualEdit(true);
                  }}
                >
                  Edit address
                </Button>
              </div>

              <AddEditAddressContactsControl
                contacts={selectedAddress?.contacts || []}
                onUpdate={handleUpdateContacts}
              />
            </Conditional>
          </div>
        </Modal.Body>

        {onDone && (
          <Modal.Footer>
            <Modal.Footer.Actions>
              <Button onClick={onDone}>Done</Button>
            </Modal.Footer.Actions>
          </Modal.Footer>
        )}
      </Conditional>
    </Modal>
  );
};

export default SelectEditAddressAndAddressContactsModal;
